import { combineReducers, configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import type { Action, AnyAction, Middleware } from 'redux';
import logger from 'redux-logger';
import { createTransform, persistReducer, persistStore } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';
import thunk, { type ThunkDispatch } from 'redux-thunk';

import { reducer as universes, initialState as universesInitialState } from '@wecasa/common/src/ducks/common/universes';
import { reducer as basket, initialState as basketInitialState } from '@wecasa/common/src/ducks/customer/basket';
import {
  reducer as proAccounts,
  initialState as proAccountsInitialState,
} from '@wecasa/common/src/ducks/customer/pro-accounts';
import { deserialize, serialize } from '@wecasa/common/src/utils/serializer';

import Config from './utils/config';

const appReducer = combineReducers({
  universes,
  proAccounts,
  basket,
});

// TODO: Test if we can remove the previousPros duck here without introducing new bugs
const persistConfig = {
  key: 'root',
  storage,
  stateReconciler: autoMergeLevel2,
  whitelist: ['basket', 'previousPros', 'proAccounts', 'test'],
  transforms: [
    createTransform(
      // transform state on its way to being serialized and persisted.
      (inboundState) => serialize(inboundState),
      // transform state being rehydrated
      (outboundState) => deserialize(outboundState),
      // define which reducers this transform gets called for.
      { blacklist: [] },
    ),
  ],
};

/*
 * When the users logs out we need to reset all the state except the parts that was initialized at
 * startup, before the users logs in. To avoid to do this in every reducer we centralize this
 * behavior.
 * This code is based on the article https://alligator.io/redux/reset-state-redux/
 */
export const rootReducer = (state: any, action: Action<any>) => appReducer(state, action);

const middlewares: Middleware[] = [thunk];

if (Config.ENABLE_REDUX_LOGGER === 'true') {
  middlewares.push(logger);
}

export type RootState = ReturnType<typeof rootReducer>;

export const DEFAULT_INITIAL_STATE: RootState = {
  universes: {
    ...universesInitialState,
  },
  proAccounts: {
    ...proAccountsInitialState,
  },
  basket: {
    ...basketInitialState,
  },
};

const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer);

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
  stateTransformer: (state) => ({ ...state, universes: null, config: null }),
});

export const createStore = (initialState?: RootState) =>
  configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false, // Disable default serializability check middleware
        immutableCheck: false, // Disable default immutability check middleware
      }).concat(middlewares),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers.concat(sentryReduxEnhancer),
    preloadedState: initialState,
    devTools: import.meta.env.MODE !== 'production',
  });

const store = createStore();

export type AppDispatch = ThunkDispatch<RootState, any, AnyAction>;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default function initializeStore() {
  const persistor = persistStore(store);
  return { store, persistor };
}
export const useAppDispatch: () => AppDispatch = useDispatch;
